import React from "react"
import { makeStyles } from "tss-react/mui"
import { Box, Divider, Stack, Typography } from "@mui/material"
import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { TranslationKey } from "../../../../../../modules/localization/translations"
import { BillReceiversItemMS, BillReceiverTypeMS } from "../../../createOrderTypes"

const useStyles = makeStyles()((theme) => ({
    li: {
        borderBottom: `1px solid ${theme.palette.tailwind.slate[200]}`,
        "&.MuiAutocomplete-option": {
            padding: ".75rem 1rem !important",
        },
    },
}))

function InlineLabel({ id, children }: { id: TranslationKey; children: string }) {
    return (
        <LocalizedStrict id={id}>
            <Typography component="span" variant="inherit">
                {children}
            </Typography>
        </LocalizedStrict>
    )
}

function InfoItem({
    labelId,
    labelText,
    value,
}: {
    labelId: TranslationKey
    labelText: string
    value: React.ReactNode
}) {
    return (
        <Typography variant="body2" component="span" color="textSecondary">
            <InlineLabel id={labelId}>{labelText}</InlineLabel> <Box component="span">{value ?? "—"}</Box>
        </Typography>
    )
}

type Props = React.HTMLAttributes<HTMLLIElement> & {
    option: BillReceiversItemMS
}

export function BillReceiverOptionItem({ option, ...liProps }: Props) {
    const { classes, cx } = useStyles()

    const { name, endpoint, contactDetails, address } = option

    const isEAN = endpoint.type === BillReceiverTypeMS.EAN_NUMBER
    const endpointLabelId = isEAN ? "create-order-bill-receiver-ean" : "create-order-bill-receiver-email"
    const endpointLabelText = isEAN ? "EAN:" : "E-mail:"
    const endpointValue = isEAN ? endpoint.ean : endpoint.email

    return (
        <li {...liProps} className={cx(liProps.className, classes.li)}>
            <Stack gap=".25rem">
                <Typography variant="subtitle1">{name}</Typography>

                {address?.displayName && (
                    <Typography variant="body2" color="textSecondary">
                        {address.displayName}
                    </Typography>
                )}

                <Stack flexDirection="row" columnGap=".75rem" flexWrap="wrap">
                    <InfoItem labelId={endpointLabelId} labelText={endpointLabelText} value={endpointValue} />

                    {contactDetails?.name && (
                        <>
                            <Divider orientation="vertical" flexItem />
                            <InfoItem
                                labelId="create-order-bill-receiver-contact"
                                labelText="Contact:"
                                value={contactDetails.name}
                            />
                        </>
                    )}
                </Stack>
            </Stack>
        </li>
    )
}
